import React, { Component } from 'react';
import { func, bool } from 'prop-types';
import { connect } from 'react-redux';
import { submit } from 'redux-form';
import { createSelector } from 'reselect';
import { formatDateFullFormat } from '@widergy/web-utils/lib/date';

import { actionCreators as userFormActionCreators, formResponsePropTypes } from 'redux/userFormHandlers';
import { actionCreators as accountActions, accountPropTypes } from 'redux/accountHandlers';
import {
  CLAIM,
  CANCELATION_COMMENTS,
  REITERATION_COMMENTS,
  CANCELATION,
  REITERATION
} from 'constants/userForms';
import SecondaryTopbar from 'app/components/SecondaryTopbar';
import AccountSelect from 'app/components/AccountSelect';
import UserFormCreator from 'app/components/UserFormCreator';
import withPagination from 'app/components/UTPagination/HOCs/pagination';
import { ROUTES } from 'constants/routes';
import WithoutAccounts from 'app/components/WithoutAccounts';
import { message } from 'utils/stringUtils';
import { appConfig } from 'config/customization';
import { CLAIMS_CONFIRM_FORM } from 'constants/reduxForms';
import { gaTrackPageView } from 'services/AnalyticsService';

import ClaimsScene from './layout';
import styles from './styles';

class ClaimsSceneContainer extends Component {
  state = {
    actionRowModalIsOpen: false,
    formId: null,
    formName: null,
    buttonClicked: null
  };

  componentDidMount() {
    this.props.getFormCategories();
    gaTrackPageView(ROUTES.CLAIMS);
    // When you change account from another component
    if (this.props.currentAccount && !this.props.currentAccount.claimsUpdated) {
      this.props.onMount();
    }
  }

  componentDidUpdate(prevProps) {
    // When you change account in this component
    if (prevProps.currentAccount && prevProps.currentAccount.id !== this.props.currentAccount.id) {
      this.props.onMount();
    }
  }

  handleChangeAccount = account => {
    this.props.changeAccount(account);
    this.props.onMount();
  };

  handleCloseActionRowModal = () => this.setState({ actionRowModalIsOpen: false });

  handleOpenActionRowModal = (id, button, formName) => {
    this.setState({ actionRowModalIsOpen: true, formId: id, formName });
    switch (button) {
      case CANCELATION:
        this.setState({ buttonClicked: CANCELATION });
        break;
      case REITERATION:
        this.setState({ buttonClicked: REITERATION });
        break;
      default:
    }
  };

  handleConfirmAction = values => {
    switch (this.state.buttonClicked) {
      case CANCELATION:
        this.props.onCancel(this.state.formId, values[CANCELATION_COMMENTS], this.state.formName);
        break;
      case REITERATION:
        this.props.onReiteration(this.state.formId, values[REITERATION_COMMENTS], this.state.formName);
        break;
      default:
    }
    this.handleCloseActionRowModal();
  };

  selectActionOptions = buttonClicked => {
    let actionOptions;
    switch (buttonClicked) {
      case CANCELATION:
        actionOptions = {
          acceptOnPress: appConfig.claims.cancelClaimComment
            ? this.props.handleSubmit
            : this.handleConfirmAction,
          modalTitle: message('Commons:cancel'),
          contentLabel: message('Commons:cancel'),
          placeholder: message('FieldDetails:cancelPlaceholder'),
          name: CANCELATION_COMMENTS,
          message: message('Claims:askCancelAction'),
          withComment: appConfig.claims.cancelClaimComment
        };
        break;
      case REITERATION:
        actionOptions = {
          acceptOnPress: appConfig.claims.reiterationClaimComment
            ? this.props.handleSubmit
            : this.handleConfirmAction,
          modalTitle: message('FieldDetails:reiterate'),
          contentLabel: message('FieldDetails:reiterate'),
          placeholder: message('FieldDetails:reiteratePlaceholder'),
          name: REITERATION_COMMENTS,
          message: message('Claims:askReiterationAction'),
          withComment: appConfig.claims.reiterationClaimComment
        };
        break;
      default:
    }
    return actionOptions;
  };

  render() {
    const { rows, onRowsRendered, isPublic } = this.props;

    return (
      <div style={styles.container}>
        <SecondaryTopbar titleText={message(`FieldDetails:${CLAIM}`)}>
          {!isPublic && appConfig.claims.showAccountSelector && (
            <AccountSelect
              filterPlaceholder={message('AccountStatus:filterPlaceholder')}
              selectedOption={this.props.currentAccount}
              dualLabel={['client_number', 'address']}
              options={this.props.accounts}
              onSelect={this.handleChangeAccount}
              keyProp="id"
              style={styles.selectContainer}
            />
          )}
          <div style={!isPublic ? styles.formCreator : null}>
            <UserFormCreator formType={CLAIM} handleFormSelected={this.props.generateClaim} />
          </div>
        </SecondaryTopbar>
        {!isPublic && (
          <ClaimsScene
            loading={this.props.loading}
            claims={rows}
            onRowsRendered={onRowsRendered}
            actionRowModalIsOpen={this.state.actionRowModalIsOpen}
            onCloseActionModal={this.handleCloseActionRowModal}
            onOpenActionModal={this.handleOpenActionRowModal}
            onConfirmAction={this.handleConfirmAction}
            actionOptions={this.selectActionOptions(this.state.buttonClicked)}
            appending={this.props.appending}
          />
        )}
      </div>
    );
  }
}

const claimSelector = createSelector(
  store => store.userForm[`${CLAIM}FormResponses`],
  claims =>
    claims.map(claim => ({
      ...claim,
      started_at: formatDateFullFormat(claim.started_at),
      status: {
        description: message(`FieldDetails:${claim.status}`),
        isPending: message(`FieldDetails:${claim.status}`) === message('FieldDetails:pending')
      }
    }))
);

const mapStateToProps = store => ({
  rows: claimSelector(store),
  formCategories: store.userForm.claimFormCategories,
  loading: store.userForm[`${CLAIM}FormResponsesLoading`],
  accounts: store.account.accounts,
  accountsLoading: store.account.accountsLoading,
  currentAccount: store.account.currentAccount,
  appending: store.account.appendFormResponsesLoading,
  accountsError: store.account.accountsError
});

const mapDispatchToProps = (dispatch, ownProps) => {
  if (!ownProps.isPublic) {
    return {
      onMount: pageSize => dispatch(userFormActionCreators.getFormResponses({ type: CLAIM, pageSize })),
      onCancel: (id, comment, formName) =>
        dispatch(userFormActionCreators.cancelation(CLAIM, id, comment, formName)),
      onReiteration: (id, comment, formName) =>
        dispatch(userFormActionCreators.reiteration(CLAIM, id, comment, formName)),
      append: (pageNumber, pageSize) =>
        dispatch(userFormActionCreators.appendFormResponses({ type: CLAIM, pageNumber, pageSize })),
      getFormCategories: () => dispatch(userFormActionCreators.getFormCategories(CLAIM)),
      changeAccount: account => dispatch(accountActions.changeCurrentAccount(account)),
      generateClaim: formId => dispatch(userFormActionCreators.createForm(formId, CLAIM, ROUTES.NEW_FORM)),
      handleSubmit: () => dispatch(submit(CLAIMS_CONFIRM_FORM))
    };
  }
  return {
    onMount: () => {},
    append: () => {},
    getFormCategories: () => dispatch(userFormActionCreators.getPublicFormCategories(CLAIM)),
    generateClaim: formId =>
      dispatch(userFormActionCreators.createForm(formId, CLAIM, ROUTES.PUBLIC_NEW_FORM))
  };
};

ClaimsSceneContainer.propTypes = {
  loading: bool,
  rows: formResponsePropTypes,
  onCancel: func,
  onReiteration: func,
  onRowsRendered: func,
  getFormCategories: func,
  changeAccount: func,
  onMount: func.isRequired,
  accounts: accountPropTypes,
  currentAccount: accountPropTypes,
  generateClaim: func,
  isPublic: bool,
  handleSubmit: func,
  appending: bool
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  WithoutAccounts({
    shouldShowMessage: props => props.accounts.length === 0 && !props.accountsLoading && !props.isPublic,
    accountsError: props => props.accountsError
  })(withPagination(ClaimsSceneContainer, 20, false, false))
);
